/**
 * useFolderPreferences Hook
 *
 * Persists folder view preferences to localStorage:
 * - View mode (card/list)
 * - Sort field and direction
 * - Show empty folders toggle
 * - Last folder ID
 * - Last active tab
 *
 * This ensures user preferences are maintained across page navigation.
 */

import { useState, useCallback, useEffect } from "react";
import { storage } from "@/lib/local-storage";

export type FolderSortField = "name" | "documentCount" | "updatedAt";
export type SortDirection = "asc" | "desc";
export type ViewMode = "card" | "list";
export type DriveTab = "myDrive" | "unmapped";

export interface FolderPreferences {
    viewMode: ViewMode;
    sortField: FolderSortField;
    sortDirection: SortDirection;
    showEmptyFolders: boolean;
    showInactiveContainers: boolean;
    lastFolderId: string | null;
    lastTab: DriveTab;
}

const STORAGE_KEY = "folderPreferences";

const DEFAULT_PREFERENCES: FolderPreferences = {
    viewMode: "card",
    sortField: "updatedAt",
    sortDirection: "desc",
    showEmptyFolders: true,
    showInactiveContainers: false,
    lastFolderId: null,
    lastTab: "myDrive",
};

/**
 * Hook for managing folder view preferences with localStorage persistence.
 *
 * @example
 * ```tsx
 * const {
 *   viewMode,
 *   setViewMode,
 *   sortField,
 *   setSortField,
 *   lastFolderId,
 *   setLastFolderId,
 * } = useFolderPreferences();
 *
 * // Use in state initialization
 * const [view, setView] = useState(viewMode);
 *
 * // Update on change
 * useEffect(() => {
 *   setViewMode(view);
 * }, [view, setViewMode]);
 * ```
 */
export const useFolderPreferences = () => {
    const [preferences, setPreferencesState] = useState<FolderPreferences>(() => {
        const stored = storage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Merge with defaults to handle missing fields from old versions
                return { ...DEFAULT_PREFERENCES, ...parsed };
            } catch {
                // Ignore parse errors, use defaults
            }
        }
        return DEFAULT_PREFERENCES;
    });

    // Persist to localStorage when preferences change
    useEffect(() => {
        storage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }, [preferences]);

    const updatePreferences = useCallback((updates: Partial<FolderPreferences>) => {
        setPreferencesState((prev) => {
            const next = { ...prev, ...updates };
            return next;
        });
    }, []);

    // Individual setters for convenience
    const setViewMode = useCallback(
        (v: ViewMode) => updatePreferences({ viewMode: v }),
        [updatePreferences]
    );

    const setSortField = useCallback(
        (f: FolderSortField) => updatePreferences({ sortField: f }),
        [updatePreferences]
    );

    const setSortDirection = useCallback(
        (d: SortDirection) => updatePreferences({ sortDirection: d }),
        [updatePreferences]
    );

    const setShowEmptyFolders = useCallback(
        (s: boolean) => updatePreferences({ showEmptyFolders: s }),
        [updatePreferences]
    );

    const setShowInactiveContainers = useCallback(
        (s: boolean) => updatePreferences({ showInactiveContainers: s }),
        [updatePreferences]
    );

    const setLastFolderId = useCallback(
        (id: string | null) => updatePreferences({ lastFolderId: id }),
        [updatePreferences]
    );

    const setLastTab = useCallback(
        (t: DriveTab) => updatePreferences({ lastTab: t }),
        [updatePreferences]
    );

    const resetPreferences = useCallback(() => {
        setPreferencesState(DEFAULT_PREFERENCES);
    }, []);

    return {
        // Current values
        ...preferences,

        // Individual setters
        setViewMode,
        setSortField,
        setSortDirection,
        setShowEmptyFolders,
        setShowInactiveContainers,
        setLastFolderId,
        setLastTab,

        // Bulk update
        updatePreferences,

        // Reset to defaults
        resetPreferences,
    };
};

export default useFolderPreferences;
